import { createBrowserRouter, type RouteObject, Outlet } from 'react-router-dom'
import { Fragment, useEffect } from 'react'
import { AppLayout } from './components/layout/app-layout'
import { ErrorBoundary, type ErrorFallbackProps } from './components/features/error-boundary'
import { attemptChunkReload, isChunkLoadError } from './lib/chunk-load-recovery'
import { ProtectedLayout } from './components/auth/protected-layout'
import { LoginForm, SignupForm, ForgotPasswordForm, ResetPasswordForm } from './components/auth'
import { InviteAcceptPage } from './components/auth/invite-accept-page'
import { McpOAuthBridgePage } from './components/auth/mcp-oauth-bridge-page'
import { ConnectCursorPage } from './components/auth/connect-cursor-page'
// Custom error fallback component for routes
function RouteErrorFallback({ error }: ErrorFallbackProps) {
  const isStaleChunk = isChunkLoadError(error)

  useEffect(() => {
    if (isStaleChunk) {
      attemptChunkReload()
    }
  }, [isStaleChunk])

  return (
    <div className="p-4 text-red-500">
      {isStaleChunk
        ? 'A new version was deployed. Refreshing...'
        : 'Something went wrong. Please refresh the page.'}
      {!isStaleChunk && (
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="ml-2 underline"
        >
          Refresh
        </button>
      )}
    </div>
  )
}

/**
 * `/oauth/basecamp-done` and `/oauth/google-drive-done` render outside `RouterProvider` in `App.tsx` (API 302 landing).
 * `/mcp-oauth` is public (outside ProtectedLayout) so Cursor OAuth can survive a login step.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <Outlet />,
    children: [
      { path: 'login', element: <LoginForm /> },
      { path: 'signup', element: <SignupForm /> },
      { path: 'forgot-password', element: <ForgotPasswordForm /> },
      { path: 'reset-password', element: <ResetPasswordForm /> },
      { path: 'invites/accept', element: <InviteAcceptPage /> },
      {
        path: 'mcp-oauth',
        element: <McpOAuthBridgePage />,
      },
      {
        path: 'connect/cursor',
        element: <ConnectCursorPage />,
      },
      {
        element: (
          <ProtectedLayout>
            <ErrorBoundary fallback={RouteErrorFallback}>
              <div className="h-full min-h-0 w-full overflow-hidden bg-background">
                <AppLayout />
              </div>
            </ErrorBoundary>
          </ProtectedLayout>
        ),
        errorElement: (
          <div className="h-screen w-full flex items-center justify-center bg-background">
            <div className="text-center p-8">
              <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
              <p className="text-muted-foreground mb-4">An error occurred while loading the page.</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Refresh Page
              </button>
            </div>
          </div>
        ),
        children: [
          {
            index: true,
            element: <Fragment />,
          },
          {
            path: 'doc/*',
            element: <Fragment />,
          },
          {
            path: 'explore/*',
            element: <Fragment />,
          },
          {
            path: 'explore',
            element: <Fragment />,
          },
          {
            path: 'compare/:doc1/:doc2',
            element: <Fragment />,
          },
          {
            path: 'basecamp',
            children: [
              {
                index: true,
                element: <Fragment />,
              },
              { path: 'bookmarks', element: <Fragment /> },
              { path: 'mystuff', element: <Fragment /> },
              { path: 'todos', element: <Fragment /> },
              { path: 'projects', element: <Fragment /> },
              {
                path: 'project/:projectId',
                element: <Fragment />,
              },
              {
                path: 'project/:projectId/todo-list/:listId',
                element: <Fragment />,
              },
              {
                path: 'project/:projectId/todo/:todoId',
                element: <Fragment />,
              },
              {
                path: 'triage',
                element: <Fragment />,
              },
              {
                path: 'todo/:todoId',
                element: <Fragment />,
              },
              { path: ':wsId/bookmarks', element: <Fragment /> },
              { path: ':wsId/mystuff', element: <Fragment /> },
              { path: ':wsId/todos', element: <Fragment /> },
              { path: ':wsId/projects', element: <Fragment /> },
              {
                path: ':wsId/project/:projectId',
                element: <Fragment />,
              },
              {
                path: ':wsId/project/:projectId/todo-list/:listId',
                element: <Fragment />,
              },
              {
                path: ':wsId/project/:projectId/todo/:todoId',
                element: <Fragment />,
              },
              {
                path: ':wsId',
                element: <Fragment />,
              },
            ],
          },
          {
            path: 'workspace/:wsId',
            element: <Fragment />,
          },
          {
            path: 'voice-memos',
            element: <Fragment />,
          },
          {
            path: 'integrations',
            children: [
              {
                index: true,
                element: <Fragment />,
              },
              {
                path: ':integrationId',
                element: <Fragment />,
              },
            ],
          },
          {
            path: 'search',
            element: <Fragment />,
          },
          {
            path: 'inbox',
            children: [
              { index: true, element: <Fragment /> },
              { path: 'thread/:threadId', element: <Fragment /> },
            ],
          },
          {
            path: 'chat',
            children: [
              { path: 'thread/:threadId', element: <Fragment /> },
            ],
          },
          {
            path: 'folder/:folderId',
            element: <Fragment />,
          },
          {
            path: 'settings',
            element: <Fragment />,
          },
          {
            path: 'product-board',
            element: <Fragment />,
          },
          {
            path: 'people',
            element: <Fragment />,
          },
          {
            path: 'people/:userId',
            element: <Fragment />,
          },
          {
            path: ':workspaceSlug',
            children: [
              { index: true, element: <Fragment /> },
              {
                path: 'basecamp',
                children: [
                  { index: true, element: <Fragment /> },
                  { path: 'bookmarks', element: <Fragment /> },
                  { path: 'mystuff', element: <Fragment /> },
                  { path: 'todos', element: <Fragment /> },
                  { path: 'projects', element: <Fragment /> },
                  { path: 'project/:projectId', element: <Fragment /> },
                  {
                    path: 'project/:projectId/todo-list/:listId',
                    element: <Fragment />,
                  },
                  {
                    path: 'project/:projectId/todo/:todoId',
                    element: <Fragment />,
                  },
                  { path: 'triage', element: <Fragment /> },
                  { path: 'todo/:todoId', element: <Fragment /> },
                ],
              },
              { path: 'voice-memos', element: <Fragment /> },
              { path: 'product-board', element: <Fragment /> },
            ],
          },
          {
            path: '*',
            element: <Fragment />,
          },
        ],
      },
    ],
  },
]

export const router = createBrowserRouter(routes)
